// src/pages/Home/ApplicationsShowcase.jsx

import React, { useMemo, useState } from "react";
import { FaArrowRight } from "react-icons/fa";

import Reveal from "../../components/ui/Reveal";
import ApplicationEditorialCard from "./components/ApplicationEditorialCard";

import { applicationFilters, applications } from "./data/applicationsData";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const INITIAL_VISIBLE_COUNT = 6;

export default function ApplicationsShowcase() {
  const [activeTarget, setActiveTarget] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const filteredApplications = useMemo(() => {
    if (activeTarget === "all") return applications;

    return applications.filter((application) =>
      application.filterKeys.includes(activeTarget),
    );
  }, [activeTarget]);

  const visibleApplications = useMemo(() => {
    if (activeTarget !== "all" || showAll) {
      return filteredApplications;
    }

    return filteredApplications.slice(0, INITIAL_VISIBLE_COUNT);
  }, [activeTarget, filteredApplications, showAll]);

  const hiddenApplicationsCount =
    filteredApplications.length - visibleApplications.length;

  const handleFilterChange = (filterKey) => {
    setActiveTarget(filterKey);
    setShowAll(false);
  };

  return (
    <section
      id="products"
      className="scroll-mt-24 bg-white py-20 dark:bg-slate-950 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col gap-8 border-b border-slate-200 pb-9 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                L’écosystème MonAppliClub
              </p>

              <h2 className="mt-4 text-4xl font-semibold leading-[1.08] tracking-[-0.035em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Une application pour
                <span className="block text-[#0868f2] dark:text-blue-300">
                  chaque besoin de terrain.
                </span>
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-slate-500 dark:text-slate-400">
              Des outils spécialisés pour les clubs, associations, collectivités
              et instances sportives.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="flex min-w-max items-center gap-7 border-b border-slate-200 dark:border-white/10">
              {applicationFilters.map((filter) => {
                const isActive = activeTarget === filter.key;

                return (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => handleFilterChange(filter.key)}
                    className={cx(
                      "relative pb-4 text-sm font-medium transition-colors",
                      isActive
                        ? "text-[#0868f2] dark:text-blue-300"
                        : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
                    )}
                  >
                    {filter.label}

                    <span
                      className={cx(
                        "absolute inset-x-0 -bottom-px h-[2px] bg-[#ff7900] transition-transform duration-300",
                        isActive ? "scale-x-100" : "scale-x-0",
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-7 lg:gap-y-14">
          {visibleApplications.map((app, index) => (
            <Reveal key={app.name}>
              <ApplicationEditorialCard app={app} priority={index < 3} />
            </Reveal>
          ))}
        </div>

        {filteredApplications.length === 0 ? (
          <div className="mt-12 border-y border-slate-200 py-12 text-center dark:border-white/10">
            <h3 className="text-2xl font-semibold text-slate-950 dark:text-white">
              Aucune application dans cette catégorie.
            </h3>

            <a
              href="#contact"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0868f2] hover:text-[#0648b8] dark:text-blue-300"
            >
              Présenter mon projet
              <FaArrowRight className="text-[10px]" />
            </a>
          </div>
        ) : null}

        {activeTarget === "all" &&
        filteredApplications.length > INITIAL_VISIBLE_COUNT ? (
          <Reveal className="mt-14">
            <div className="flex flex-col gap-5 border-t border-slate-200 pt-8 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {showAll
                  ? `${filteredApplications.length} applications présentées`
                  : `${hiddenApplicationsCount} autres applications disponibles`}
              </p>

              <button
                type="button"
                onClick={() => setShowAll((current) => !current)}
                className="group inline-flex items-center gap-3 self-start text-sm font-semibold text-slate-950 transition hover:text-[#0868f2] dark:text-white dark:hover:text-blue-300 sm:self-auto"
              >
                {showAll ? "Réduire la sélection" : "Découvrir toute la gamme"}

                <span className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-[#0868f2] transition group-hover:border-[#0868f2] group-hover:bg-[#0868f2] group-hover:text-white dark:border-white/10 dark:text-blue-300">
                  <FaArrowRight
                    className={cx(
                      "text-[10px] transition-transform",
                      showAll && "rotate-180",
                    )}
                  />
                </span>
              </button>
            </div>
          </Reveal>
        ) : null}
      </div>
    </section>
  );
}
